import { useState } from "react";
import { Copy, RefreshCw, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import { Stat } from "@/components/tools/fields";
import { ROLE_LABEL } from "@/context/WorkspaceContext";

const AccessCode = ({ code, onChange }) => {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success("Access code copied");
    } catch {
      toast.error("Could not copy. Please select and copy the code.");
    }
  };
  const regenerate = async () => {
    if (!window.confirm("Create a new access code? Staff and governors will need the new code next time they sign in.")) return;
    try {
      await api.post("/rc/access-code");
      toast.success("New access code created");
      onChange();
    } catch (err) {
      toast.error(errorText(err));
    }
  };
  return (
    <div className="rounded-2xl border-2 border-line bg-paper p-5" data-testid="ws-access-code-card">
      <p className="flex items-center gap-2 text-sm font-bold text-ink-soft"><KeyRound size={16} aria-hidden /> Organisation access code</p>
      <p className="mt-2 break-all font-display text-2xl font-semibold tracking-wide sm:text-3xl" data-testid="ws-access-code">{code}</p>
      <p className="mt-2 text-sm text-ink-soft">Share this with staff and governors so they can join and sign in.</p>
      <div className="mt-4 flex flex-wrap gap-2">
        <button type="button" onClick={copy} className="btn btn-ghost btn-sm" data-testid="ws-access-code-copy"><Copy size={14} aria-hidden /> Copy</button>
        <button type="button" onClick={regenerate} className="btn btn-ghost btn-sm" data-testid="ws-access-code-regenerate"><RefreshCw size={14} aria-hidden /> New code</button>
      </div>
    </div>
  );
};

const Identity = ({ org }) => (
  <dl className="grid gap-3 rounded-2xl border-2 border-line bg-paper p-5 text-sm sm:grid-cols-2" data-testid="ws-identity">
    {[["Tenant ID", org.tenant_id], ["Organisation ID", org.id], ["Workspace ID", org.workspace_id], ["Encryption scope", org.encryption_scope]].map(([k, v]) => (
      <div key={k}><dt className="font-bold text-ink-soft">{k}</dt><dd className="break-all font-mono">{v}</dd></div>
    ))}
  </dl>
);

export const Overview = ({ ws, data, reload, refresh, manage }) => {
  const [n] = useState(() => new Date().getHours());
  const greet = n < 12 ? "Good morning" : n < 18 ? "Good afternoon" : "Good evening";
  const count = (s) => data.ehcp.filter((r) => r.status === s).length;
  return (
    <section id="ws-overview" aria-labelledby="ws-title" className="scroll-mt-[150px]">
      <p className="eyebrow mb-4">{ROLE_LABEL[ws.user.role]} · Secure workspace</p>
      <h1 id="ws-title" className="font-display text-[2.4rem] font-medium leading-[1] tracking-[-0.03em] sm:text-6xl" data-testid="ws-school-name">
        {ws.org.name}
      </h1>
      <p className="mt-4 text-lg text-ink-soft">{greet}, {ws.user.name}. Only people from your school can see anything in this workspace.</p>
      <div className="mt-8 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="EHCP reviews" value={data.ehcp.length} testid="ws-stat-ehcp" />
        <Stat label="Overdue" value={count("overdue")} tone="red" testid="ws-stat-overdue" />
        <Stat label="Due this month" value={count("due_this_month")} tone="amber" testid="ws-stat-month" />
        <Stat label="Evidence items" value={data.evidence.length} testid="ws-stat-evidence" />
      </div>
      <div className={`mt-6 grid gap-4 ${manage ? "lg:grid-cols-2" : ""}`}>
        {manage && <AccessCode code={ws.org.access_code} onChange={() => { refresh(); reload(); }} />}
        <Identity org={ws.org} />
      </div>
    </section>
  );
};
